import { useEffect, useRef, useState } from "react";
import { Modal, Segmented, Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import _ from "lodash";
import {
  ACCEPTED_IMAGE_TYPES,
  rejectInvalidImage,
  updateBrickCollection,
  uploadImage,
} from "../../utils";
import useOrganization from "../../hooks/useOrganization";
import type { BrickType } from "../../masterImages/types";

interface Props {
  brickCollection: any | null;
  open: boolean;
  onClose(): void;
  onReload(): void;
}

const UploadBricksModal: React.FC<Props> = ({
  brickCollection,
  open,
  onClose,
  onReload,
}) => {
  const organization = useOrganization();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [types, setTypes] = useState<Record<string, BrickType>>({});
  const keys = useRef<Record<string, string>>({});

  useEffect(() => {
    setSubmitting(false);
    setTypes({});
  }, [open]);

  const handleFileChanged = ({ fileList }: { fileList: any[] }) => {
    setFileList(fileList);
  };

  const handleUpload = async ({
    onSuccess,
    onError,
    onProgress,
    file,
  }: any) => {
    try {
      keys.current[file.uid] = await uploadImage(
        organization,
        brickCollection._id,
        "brick",
        file,
        (percent) => onProgress({ percent }, file)
      );
      onSuccess(null, file);
    } catch (e) {
      onError(e, null, file);
    }
  };

  const submitBricks = async () => {
    setSubmitting(true);
    try {
      await updateBrickCollection(organization, brickCollection._id, {
        bricks: [
          ...brickCollection.bricks,
          ...fileList.map((file) => ({
            type: types[file.uid] ?? "full",
            image: keys.current[file.uid],
          })),
        ],
      });
      onClose();
    } catch (e) {
      message.error((e as Error).message);
      onReload();
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={submitBricks}
      okButtonProps={{
        disabled: submitting || !_.every(fileList, (f) => f.status === "done"),
      }}
      okText="Klaar"
      closable={false}
      destroyOnHidden
    >
      <Upload.Dragger
        accept={ACCEPTED_IMAGE_TYPES}
        beforeUpload={rejectInvalidImage}
        listType="picture"
        itemRender={(node, file) => (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1 }}>{node}</div>
            <Segmented
              size="small"
              options={[
                { label: "Full", value: "full" },
                { label: "Half", value: "half" },
              ]}
              value={types[file.uid] ?? "full"}
              onChange={(value) => setTypes((t) => ({ ...t, [file.uid]: value as BrickType }))}
            />
          </div>
        )}
        multiple
        showUploadList={{
          showDownloadIcon: false,
          showPreviewIcon: true,
          showRemoveIcon: true,
        }}
        customRequest={(e) => handleUpload(e)}
        onChange={(e) => handleFileChanged(e)}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Klik of sleep een bestand hier om te uploaden
        </p>
      </Upload.Dragger>
    </Modal>
  );
};

export default UploadBricksModal;
